/**
 * User-facing copy for server CSV export wait time (phone + Voluum bulk).
 * For Media Devoted | Created By Ciro Autiero
 */

export interface ServerCsvExportDurationHint {
  /** e.g. "about 3–6 minutes" */
  minutesLine: string;
  /** Plain-language why it can take a while */
  whyLine: string;
}

const NF = new Intl.NumberFormat();

/**
 * ~400k rows → ~3 min centre; ~1.5M → ~10 min; capped 1–20 min band.
 */
export function formatServerCsvExportDurationHint(
  estimatedRowCount: number | null | undefined,
): ServerCsvExportDurationHint {
  const rows = estimatedRowCount ?? 0;
  if (!Number.isFinite(rows) || rows <= 0) {
    return {
      minutesLine: 'This usually takes a few minutes for large exports.',
      whyLine:
        'We pull the full report from Voluum for your date range, then build your CSV. Bigger ranges mean more data to gather, so please keep this window open until the download starts.',
    };
  }

  const mid = Math.round(rows / 135_000);
  const center = Math.min(20, Math.max(1, mid));
  const low = Math.max(1, center - 1);
  const high = Math.min(20, center + 2);
  const minutesLine =
    low >= high
      ? `Expect roughly ${center} minute${center === 1 ? '' : 's'}.`
      : `Expect roughly ${low}–${high} minutes.`;

  const whyLine = `There are about ${NF.format(rows)} rows to include. Voluum assembles that traffic data in several steps; we then apply your filters and prepare the file. That’s why very large exports can take longer — you can leave this dialog open or switch tabs while it runs.`;

  return { minutesLine, whyLine };
}
